/**
 * useDesignWorld — Does this client get the imported design world's chrome?
 *
 * The imported design ships ONE palette and no dark variant, while the
 * product has a live light/dark toggle. Rather than invent a dark ramp the
 * designer never drew, the imported chrome is light-only and dark keeps the
 * incumbent header (Ruling R17, spec §3 "Contrast correction" neighbourhood /
 * the auth-v2.css precedent: "this design ships ONE palette... light-only").
 * Reversible: the design DOES supply a dark palette (the assistant's sidebar
 * rail, `--gd-panel` / `--gd-panel-ink`), so a future ramp can derive from
 * real values instead of an invented one.
 *
 * Resolution mirrors `ThemeController` EXACTLY (same query, same
 * matchMedia-absent fallback to "light", same "system" OS-change listener) —
 * duplicated rather than imported because `ThemeController` isn't a hook and
 * lives outside this task's allowed-touch list. If `ThemeController`'s own
 * resolution logic ever changes, this must change with it by hand.
 *
 * jsdom guard: `window.matchMedia` is undefined under Vitest's jsdom env, so
 * every access is gated on `typeof window.matchMedia === "function"`. When
 * it is absent, "system" falls back to "light" (the GardEn default theme)
 * and no listener is attached — this hook then simply returns `true`.
 */
import { useEffect, useState } from "react";

import { useUiStore, type Theme } from "@/lib/ui-store";

const COLOR_SCHEME_QUERY = "(prefers-color-scheme: dark)";

/** True when the jsdom/SSR `matchMedia` shim is unavailable. */
function hasMatchMedia(): boolean {
  return (
    typeof window !== "undefined" && typeof window.matchMedia === "function"
  );
}

/**
 * Resolve a (possibly "system") theme preference to the concrete
 * "light"/"dark" `ThemeController` itself writes to `<html data-theme>`.
 */
function resolveTheme(theme: Theme): "light" | "dark" {
  if (theme !== "system") {
    return theme;
  }
  if (hasMatchMedia()) {
    return window.matchMedia(COLOR_SCHEME_QUERY).matches ? "dark" : "light";
  }
  return "light";
}

export function useDesignWorld(): boolean {
  const theme = useUiStore((state) => state.theme);
  const [resolved, setResolved] = useState<"light" | "dark">(() =>
    resolveTheme(theme),
  );

  useEffect(() => {
    setResolved(resolveTheme(theme));

    // Only "system" needs to react to OS changes; explicit light/dark is fixed.
    if (theme !== "system" || !hasMatchMedia()) {
      return;
    }

    const mql = window.matchMedia(COLOR_SCHEME_QUERY);
    const onChange = (): void => {
      setResolved(resolveTheme(theme));
    };
    mql.addEventListener("change", onChange);
    return () => {
      mql.removeEventListener("change", onChange);
    };
  }, [theme]);

  return resolved === "light";
}

export default useDesignWorld;
